import { Component } from '@theme/component';

/**
 * An accessible tabs component for the product information area.
 *
 * Markup contract:
 * - Tab buttons: `[role="tab"][data-index]` with `on:click="/select"`
 * - Panels: `[role="tabpanel"][data-index]`, non-active ones carry `hidden`
 */
class ProductTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Declarative click handler bound via `on:click="/select"`.
   * @param {Event} event
   */
  select(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-index]');
    if (!button) return;

    this.#activate(button.getAttribute('data-index'));
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  get #panels() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tabpanel"]')));
  }

  /**
   * @param {string | null} index - The `data-index` of the tab to activate.
   */
  #activate(index) {
    if (index == null) return;

    for (const tab of this.#tabs) {
      const selected = tab.getAttribute('data-index') === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.#panels) {
      panel.toggleAttribute('hidden', panel.getAttribute('data-index') !== index);
    }
  }

  /**
   * Arrow-key / Home / End navigation across the tab list.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (current === -1) return;

    let next = current;
    switch (event.key) {
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
    }

    event.preventDefault();
    this.#activate(tabs[next].getAttribute('data-index'));
    tabs[next].focus();
  };
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
